import { useContext, useEffect, useSyncExternalStore } from 'react';
import {
    CentralizedSideEffectsContext,
    CentralizedInitCompleteContext,
    CentralizedInitExecutedKeysContext,
} from '@/context/Contexts';
import { useBecameVisible } from '@hooks/useBecameVisible';

interface ILateCascadeControl {
    key: string;
    invokeChangesOnNew?: boolean;
    invokeChangesOnEdit?: boolean;
}

/**
 * Нужно ли пропустить per-field invokeChanges-каскад контрола в централизованном режиме.
 *
 * В централизованном режиме (resolver-валидация) первичный проход invokeChanges делает
 * `runInvokeChangesInit` один раз на старте по порядку схемы, отмечая выполненные контролы
 * в реестре CentralizedInitExecutedKeysContext. После завершения прохода (`initComplete`)
 * per-field каскад разрешён в двух случаях:
 *  - `becameVisible` — контрол смонтирован и его видимость перешла false→true;
 *  - контрол виден, но его ключа нет в реестре — он был скрыт на инициализации, а переход
 *    видимости случился, пока контрол был размонтирован (скрытый контейнер, ленивая вкладка):
 *    на свежем маунте `becameVisible` этого перехода не видит. Разрешение одноразовое —
 *    контрол сразу отмечается в реестре.
 * Пока проход не завершён, раскрытие обрабатывает централизованный обход (повторными
 * свипами), поэтому per-field каскад в это время пропускаем (иначе двойной прогон —
 * `runInvokeChangesInit` не идемпотентен). Вне централизованного режима каскад не пропускаем.
 *
 * `visibilitySettled` (EUPDEV-12642) — правило видимости уже вычислено. Правила считаются
 * асинхронно и до первого результата отдают `false`, поэтому на маунте контрола видимость всегда
 * «переходит» false→true. Без этого признака такой переход выглядел как позднее появление
 * видимости и повторял уже выполненный централизованно каскад (дублирование строк таблиц).
 *
 * Гейт открывается только у контрола с каскадом в текущем режиме (invokeChangesOnNew/OnEdit): реестр чистится на
 * каждом перезапуске прохода (вход в правку), и иначе каждое поле без каскада открывало бы гейт заново и
 * перерисовывалось впустую.
 */
export function useLateVisibilityCascadeGate(
    control: ILateCascadeControl | undefined,
    isNew: boolean,
    isEdit: boolean,
    visibility: boolean | undefined,
    visibilitySettled: boolean,
): boolean {
    const controlKey = control?.key;
    const cascades = (isNew && !!control?.invokeChangesOnNew) || (isEdit && !!control?.invokeChangesOnEdit);
    const centralizedSideEffects = useContext(CentralizedSideEffectsContext);
    const initCompleteStore = useContext(CentralizedInitCompleteContext);
    const executedKeys = useContext(CentralizedInitExecutedKeysContext);
    const becameVisible = useBecameVisible(visibility, visibilitySettled);

    const isPendingInitial = () => !!controlKey && executedKeys !== null && !executedKeys.has(controlKey);
    const visibleReady = cascades && !!visibility && visibilitySettled;
    // Подписка на решение, а не на сам признак: вход в правку и выход из неё переключают initComplete, и при
    // подписке на значение перерисовывались бы все поля документа, хотя у большинства решение не меняется.
    // Реестр читается в снимке: перезапуск прохода чистит его без перерисовки поля.
    const allowCascade = useSyncExternalStore(
        initCompleteStore.subscribe,
        () => visibleReady && (becameVisible || isPendingInitial()) && initCompleteStore.get(),
    );

    // Одноразовость pendingInitial: разрешение выдано — контрол считается обработанным,
    // даже если его собственные флаги invokeChangesOn* каскад не запустят (тогда каскада
    // не было бы ни в одном режиме). Мутация Set не вызывает ре-рендер.
    useEffect(() => {
        if (centralizedSideEffects && allowCascade && controlKey && isPendingInitial()) {
            executedKeys?.add(controlKey);
        }
    });

    return centralizedSideEffects && !allowCascade;
}
